import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { LoadingArea } from '@consigli/facade';
import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { getAuthorizationCode, getSession, setSessionInStorage } from './oauth';
import { useAuthContext } from './react-context';
import { useOAuthCallback } from './use-oauth-callback';
/** Auth will be the callback from OAuth. So where we'll retrieve the token and store it */
export const AuthGuard = ({ children, authConfig, loading }) => {
    const { session, setSession } = useAuthContext();
    const { pathname } = useLocation();
    // The PDE relay (since `@aecom-ai-engineering/auth` 0.0.14) forwards the
    // OAuth `?code=` back to the protected path the user was on when login
    // started, not to a fixed `/auth` route. Run the callback hook here so
    // the exchange happens at whichever route the relay lands on.
    const { isHandlingCallback } = useOAuthCallback({ authConfig, destination: pathname });
    useEffect(() => {
        if (isHandlingCallback) {
            // useOAuthCallback is exchanging the code; don't restart the login flow.
            return;
        }
        const fetchSession = async () => {
            const sess = await getSession(authConfig);
            if (!sess) {
                /** Will be redirected to the login page in the IDP */
                await getAuthorizationCode(authConfig);
                return;
            }
            const payload = sess.idToken.payload;
            /** Assert that we have all the required fields */
            if (payload && !(payload.sub && payload.name)) {
                throw new Error(`Missing required fields in session: ${JSON.stringify(sess)}`);
            }
            setSession(sess);
            setSessionInStorage(sess);
        };
        // Storage is kept, so a reload after a network blip can still refresh
        if (session == null)
            fetchSession().catch((error) => console.error('Auth check failed:', error));
    }, [authConfig, session, setSession, isHandlingCallback]);
    if (session) {
        return _jsx(_Fragment, { children: children });
    }
    return _jsx(_Fragment, { children: loading ?? _jsx(LoadingArea, { title: "Checking authorization" }) });
};
